import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../api/axiosConfig";
import * as idb from '../../../functions/databaseService/indexedDB';
import * as Sentry from "@sentry/capacitor";


export const IMPORT_MINDMAP_SUCCESS = "IMPORT_MINDMAP_SUCCESS";
export const IMPORT_MINDMAP_ERROR = "IMPORT_MINDMAP_ERROR";


export const importMindMap = createAsyncThunk(
    "api/Task/import",
    async (file, { dispatch, rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            const online = navigator.onLine;

            if (online) {
                const response = await axios.put(
                    `${import.meta.env.VITE_PUBLIC_DOMAIN}Task/import`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                if (response.status === 200) {
                    if (response.data === '') {
                        throw new Error("The last task has not been saved. Logging the user out");
                    } else {
                        idb.storeTaskList(response.data.tasks || response.data);
                    }
                } else {
                    throw new Error("Error importing mind map");
                }

                dispatch({
                    type: IMPORT_MINDMAP_SUCCESS,
                    payload: response.data
                });
            } else {
                const taskListData = await idb.getTasks();
                if (taskListData) {
                    dispatch({
                        type: IMPORT_MINDMAP_SUCCESS,
                        payload: taskListData
                    });
                } else {
                    console.log("No tasks found in IndexedDB");
                }
            }
        } catch (error) {
            console.error('Import mind map error:', error);

            // Only capture client-side/network errors; server 500+ errors are already captured by TaskController
            if (!error.response || (error.response.status < 500 && error.response.status !== 401)) {
                Sentry.captureException(error, {
                    tags: {
                        error_type: 'mindmap_import_failure',
                        operation: 'importMindMap',
                        is_online: navigator.onLine,
                    },
                    level: 'error',
                    extra: {
                        status_code: error.response?.status,
                        file_name: file?.name,
                    }
                });
            }

            // Create user-friendly error based on server response
            let errorInfo = {
                errorCode: 'IMPORT_FAILED',
                title: 'Import failed',
                message: 'Something went wrong while importing your mind map.',
                suggestion: 'Please try again or contact support if the problem continues.'
            };

            // Handle specific server errors
            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data;

                if (status === 400 && typeof serverMessage === 'string' && serverMessage.includes('Invalid file type')) {
                    errorInfo = {
                        errorCode: 'SERVER_INVALID_FILE_TYPE',
                        title: 'File type not supported',
                        message: 'The server couldn\'t process your file. Make sure you\'re uploading a valid mind map file.',
                        suggestion: 'Please select a .mvdx, .mvd, or .inspmaps file from your mind mapping software.'
                    };
                } else if (status === 401) {
                    errorInfo = {
                        errorCode: 'UNAUTHORIZED',
                        title: 'Authorization required',
                        message: 'You need to be logged in to import files.',
                        suggestion: 'Please sign in and try again.'
                    };
                } else if (status >= 500) {
                    errorInfo = {
                        errorCode: 'SERVER_ERROR',
                        title: 'Server error',
                        message: 'Our servers are having trouble processing your request right now.',
                        suggestion: 'Please try again in a few minutes, or contact support if the issue persists.'
                    };
                }
            } else if (error.request) {
                errorInfo = {
                    errorCode: 'NETWORK_ERROR',
                    title: 'Connection problem',
                    message: 'We couldn\'t reach our servers to import your file.',
                    suggestion: 'Please check your internet connection and try again.'
                };
            }

            dispatch({
                type: IMPORT_MINDMAP_ERROR,
                payload: { ...error, userFriendlyError: errorInfo }
            });

            // Return the error info for the component to use
            return rejectWithValue(errorInfo);
        }
    }
);